import { useMemo, useState, useRef } from 'react';
import { clsx } from 'clsx';
import {
  Typography,
  Input,
  Button,
  AmountInput,
  Textarea,
  useIsMounted,
  Modal,
} from '@ywwwtseng/react-kit';
import { ImageUp, ChevronDown } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { FileInput } from './FileInput';
import { FileInputImage } from './FileInputImage';
import { DatePicker } from './DatePicker';
import { FormLabel } from './FormLabel';
import { FormElementInfo } from './FormElementInfo';
import type { FormElement, ValueType, FormValues, FormRow } from './types';
import { getDefaultValue, getErrorMessage } from './utils';

// 检测键盘是否已经打开
function isKeyboardOpen(): boolean {
  if (typeof window === 'undefined') return false;

  // 使用 visualViewport API（如果可用）来检测键盘
  if (window.visualViewport) {
    // 如果 visualViewport 高度明显小于 outerHeight，说明键盘已打开
    return window.visualViewport.height < window.outerHeight * 0.75;
  }

  // 降级方案：检查 innerHeight 是否明显小于 outerHeight
  return window.innerHeight < window.outerHeight * 0.75;
}

// 获取滚动延迟时间（根据键盘状态）
function getScrollDelay(): number {
  return isKeyboardOpen() ? 150 : 1500;
}

export function Form({
  name,
  isLoading = false,
  disabled = false,
  values,
  calculated = {},
  setValues,
  elements,
  confirm: Confirm,
  submit,
  onSubmit,
}: {
  name: string;
  isLoading?: boolean;
  disabled?: boolean;
  values: FormValues;
  calculated?: FormValues;
  setValues: (values: FormValues) => void;
  elements: FormRow[];
  confirm?: React.ElementType<{
    isLoading: boolean;
    onConfirm: () => void | Promise<void>;
  }>;
  submit?: {
    label?: string;
  };
  onSubmit?: (
    values: Record<string, ValueType | null>,
    calculated: Record<string, ValueType | null>
  ) => unknown | Promise<unknown>;
}) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isMounted = useIsMounted();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setValue = (
    name: string,
    value: ValueType | null,
    whenChange?: (
      value: ValueType | null
    ) => Record<string, ValueType | null> | undefined
  ) => {
    const needChange = whenChange ? whenChange(value) : undefined;
    const resets = elements.reduce(
      (acc: Record<string, ValueType | null>, element) => {
        element.row.forEach((e) => {
          if ('dependencies' in e && e.dependencies?.includes(name)) {
            acc[e.name] = getDefaultValue(e);
          }
        });
        return acc; // 确保 reduce 回调函数返回累加器
      },
      {} // 将初始累加器设置为空对象
    );

    setIsSubmitting(false);
    const next = { ...values, ...resets, [name]: value, ...needChange };
    setValues(next);
  };

  const errors = useMemo(() => {
    const acc: Record<string, string | boolean> = {};
    return elements
      .map((element) => element.row)
      .flat()
      .filter((element) => 'name' in element)
      .reduce((acc, element) => {
        const error = getErrorMessage(
          'validate' in element ? element.validate : undefined,
          element.name,
          { ...values, ...calculated }
        );

        if (error) {
          acc[element.name] = error;
        }

        return acc;
      }, acc);
  }, [elements, values, calculated]);

  const renderElement = (element: FormElement) => {
    switch (element.ui) {
      case 'title':
        return (
          <Typography size="3" weight={600} {...element.props}>
            {element.text}
          </Typography>
        );
      case 'description':
        return (
          <Typography size="1" color="#777777" {...element.props}>
            {element.text}
          </Typography>
        );
      case 'date':
        return (
          <div className="ml-auto flex items-center justify-end">
            <DatePicker
              label={element.label}
              value={values[element.name] as string | null}
              onChange={(value) => {
                setValue(element.name, value, element.whenChange);
              }}
              {...element.props}
            />
          </div>
        );
      case 'text':
        return (
          <Input
            aria-label={element.label}
            autoComplete="off"
            name={element.name}
            value={values[element.name] as string}
            onChange={(e) => {
              setValue(element.name, e.target.value, element.whenChange);
            }}
            {...element.props}
            className={clsx(
              'rounded-lg h-10 px-2 text-right w-full',
              element.props?.className
            )}
            onFocus={(e) => {
              const delay = getScrollDelay();
              setTimeout(() => {
                e.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, delay);
            }}
          />
        );
      case 'amount':
        return (
          <AmountInput
            aria-label={element.label}
            autoComplete="off"
            name={element.name}
            value={values[element.name] as string}
            onChange={(value) => {
              setValue(element.name, value, element.whenChange);
            }}
            {...element.props}
            className={clsx(
              'rounded-lg h-10 px-2 text-right w-full',
              element.props?.className
            )}
            onFocus={(e) => {
              const delay = getScrollDelay();
              setTimeout(() => {
                e.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, delay);
            }}
          />
        );
      case 'select':
        const selected = element.props?.options?.find((option) => option.key === values[element.name]);
        return (
          <Select
            trigger={(
              <div className="w-full flex items-center justify-between gap-1 pl-2 pr-3 bg-form-element rounded-lg text-sm h-10">
                <Typography size="2" className={values[element.name] ? 'text-foreground' : 'text-placeholder'}>{element.label}</Typography>
                <div className="flex-1 flex items-center justify-end gap-2">
                  <Typography size="2" className={selected ? 'text-foreground' : 'text-placeholder'}>{selected?.name}</Typography>
                </div>
                <ChevronDown width={20} height={20} />
              </div>
            )}
            label={element.label}
            value={values[element.name] as string}
            onChange={(value) => {
              setValue(element.name, value as string, element.whenChange);
            }}
            {...element.props}
            options={element.props?.options ?? []}
            disabled={element.props?.disabled}
          />
        );
      case 'textarea':
        return (
          <Textarea
            name="description"
            className="text-sm text-left min-h-32 max-h-32 w-full p-2"
            value={values[element.name] as string}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setValue(element.name, e.target.value, element.whenChange);
            }}
            onFocus={(e) => {
              setTimeout(() => {
                e.target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 1500);
            }}
            placeholder={element.props?.placeholder}
          />
        );
      case 'file':
        return (
          <FileInput
            onChange={(files) => {
              const file = files[0];
              if (file) {
                setValue(element.name, file, element.whenChange);
                setValue(element.name, file);
              }
            }}
            {...element.props}
          >
            {values[element.name] ? (
              <div className="v-stack w-full h-[80px] gap-1 overflow-hidden">
                <FileInputImage file={values[element.name] as File} />
              </div>
            ) : (
              <div className="v-stack w-full h-[80px] gap-1 bg-form-element rounded-lg">
                <ImageUp
                  strokeWidth={1.5}
                  className="opacity-50 text-icon-foreground"
                />
                <Typography className="opacity-50" size="1">
                  {element.props?.placeholder ?? 'gift.picture'}
                </Typography>
              </div>
            )}
          </FileInput>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="relative flex flex-col justify-start w-full h-full"
      ref={ref}
    >
      {isMounted && (
        <>
          {elements.map(({ row, className }, index) => (
            <div
              key={name + String(index)}
              className={clsx('flex w-full items-start gap-2 mb-2', className)}
            >
              {row.map((element) => {
                if (element.hidden) {
                  return null;
                }

                return (
                  <div
                    className={clsx(
                      'flex flex-col gap-0.5',
                      element.width ? 'w-full' : 'flex-1'
                    )}
                    style={{
                      width: element.width ?? 'auto',
                    }}
                    key={'name' in element ? element.name : element.text}
                  >
                    <div
                      className={clsx('w-full flex gap-1', {
                        'flex-col': element.direction === 'column',
                        'items-center': element.direction !== 'column',
                        'pl-2 bg-form-element rounded-lg text-sm': ![
                          'description',
                          'select',
                          'custom',
                        ].includes(element.ui),
                        'opacity-30': element.props?.disabled,
                      })}
                    >
                      {element.ui !== 'select' && (
                        <FormLabel element={element} />
                      )}
                      {element.render
                        ? element.render({
                          values,
                          calculated,
                          setValue,
                        })
                        : renderElement(element)}
                    </div>
                    <FormElementInfo
                      values={values}
                      calculated={calculated}
                      isSubmitting={isSubmitting}
                      element={element}
                      errors={errors}
                      description={element.description}
                    />
                  </div>
                );
              })}
            </div>
          ))}
          <div className="w-full mt-auto pb-2">
            <Button
              isLoading={isConfirmOpen ? false : isLoading}
              disabled={isLoading || disabled || Object.keys(errors).length > 0}
              width="full"
              color="primary"
              variant="contained"
              size="sm"
              rounded="full"
              onClick={() => {
                setIsSubmitting(true);

                if (Object.keys(errors).length === 0) {
                  if (Confirm) {
                    setIsConfirmOpen(true);
                  } else {
                    void onSubmit?.(values, calculated);
                  }
                }
              }}
            >
              {submit?.label ?? 'Submit'}
            </Button>
          </div>
        </>
      )}
      {Confirm && (
        <Modal
          open={isConfirmOpen}
          onClose={() => {
            setIsConfirmOpen(false);
          }}
          styles={{
            content: {
              paddingBottom: '16px',
            },
          }}
          title="Form Confirm"
        >
          <Confirm
            isLoading={isLoading}
            onConfirm={async () => {
              setIsConfirmOpen(false);
              await onSubmit?.(values, calculated);

            }}
          />
        </Modal>
      )}
    </div>
  );
}
